'use client';

import clsx from 'clsx';
import Image, { ImageProps } from 'next/image';
import { useEffect, useState } from 'react';

interface TerraImageProps extends Omit<ImageProps, 'src' | 'alt'> {
    src: string;
    alt: string;
    wrapperClassName?: string;
    imageClassName?: string;
    skeletonClassName?: string;
}

const shimmer =
    'before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.8s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/8 before:to-transparent';

export default function TerraImage({
    src,
    alt,
    wrapperClassName,
    imageClassName,
    skeletonClassName,
    className,
    onLoad,
    onError,
    ...props
}: Readonly<TerraImageProps>) {
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        setLoaded(false);
    }, [src]);

    useEffect(() => {
        if (!src) {
            return;
        }

        const image = new window.Image();
        image.src = src;

        if (image.complete && image.naturalWidth > 0) {
            setLoaded(true);
            return;
        }

        const handleLoad = () => setLoaded(true);
        const handleError = () => setLoaded(true);

        image.addEventListener('load', handleLoad);
        image.addEventListener('error', handleError);

        return () => {
            image.removeEventListener('load', handleLoad);
            image.removeEventListener('error', handleError);
        };
    }, [src]);

    return (
        <div className={clsx('relative overflow-hidden', wrapperClassName)}>
            {!loaded && (
                <div
                    aria-hidden="true"
                    className={clsx(
                        'absolute inset-0 z-10 bg-[#18181D]',
                        shimmer,
                        skeletonClassName,
                    )}
                />
            )}
            <Image
                {...props}
                src={src}
                alt={alt}
                className={clsx(
                    className,
                    imageClassName,
                    loaded ? 'opacity-100' : 'opacity-0',
                )}
                onLoad={(event) => {
                    setLoaded(true);
                    onLoad?.(event);
                }}
                onError={(event) => {
                    setLoaded(true);
                    onError?.(event);
                }}
            />
        </div>
    );
}
